import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useEffect, useState, useCallback } from 'react';
import Dashboard from './pages/Dashboard';
import DashboardV2 from './pages/DashboardV2';  // Task 35: Dashboard 2.0
import Candidates from './pages/Candidates';
import UploadPage from './pages/Upload';
import Reports from './pages/Reports';
import FollowUps from './pages/FollowUps';
import MyFollowUps from './pages/MyFollowUps';  // Task 36: Follow-Up Engine
import JDUpload from './pages/JDUpload';
import JDDetail from './pages/JDDetail';  // Task 20: Add JD detail page
import Notifications from './pages/Notifications';  // Task 21: Add Notifications page
import Settings from './pages/Settings';  // Task 21: Add Settings page
import Login from './pages/Login';
import PasswordResetPage from './pages/PasswordResetPage';  // Task 26: Password reset
import ForgotPasswordPage from './pages/ForgotPasswordPage';  // Task 28: Forgot password OTP flow
import AdminDashboard from './pages/AdminDashboard';  // Task 26: Admin dashboard
import ManagerDashboard from './pages/ManagerDashboard';  // Task 26: Manager dashboard
import AuthCallback from './pages/AuthCallback';  // Task 24: OAuth callback handler
import ProtectedRoute from './components/ProtectedRoute';
import SessionModal from './components/SessionModal';
import Layout from './components/Layout';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import PWAInstallPrompt from './components/PWAInstallPrompt';  // Task 30: PWA install prompt
import { pwaManager } from './utils/pwaUtils';  // Task 30: PWA utilities
import { AUTH_STATE_EVENT } from './utils/authSession';
// Task 31: Mobile components
import MobileDrawer from './components/mobile/MobileDrawer';  // Mobile drawer navigation
import DashboardMobile from './components/mobile/DashboardMobile';  // Mobile dashboard

// Task 17: Navigation moved to Sidebar component

// Logout Toast Component
function LogoutToast({ show, message, onClose }) {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed top-4 right-4 z-50 bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center space-x-3 animate-slide-in shadow-xl">
      <AlertCircle className="text-red-400" size={20} />
      <p className="text-red-400 font-body">{message}</p>
      <button onClick={onClose} className="text-red-400 hover:opacity-70 transition-opacity ml-2">
        ×
      </button>
    </div>
  );
}

function AppContent() {
  // Initialize session timeout hook
  const {
    showWarningModal,
    countdown,
    showLogoutToast,
    logoutMessage,
    handleStayLoggedIn,
    handleLogoutNow,
  } = useSessionTimeout();

  // Task 31: Mobile state management
  const [isMobile, setIsMobile] = useState(false);
  const [userData, setUserData] = useState(null);
  const [notificationCount, setNotificationCount] = useState(3);

  // Task 30: Initialize PWA manager
  useEffect(() => {
    // PWA manager is automatically initialized when imported
    console.log('[App] PWA initialized');
    
    // Request notification permission for future push notifications
    if ('Notification' in window && Notification.permission === 'default') {
      // Don't request immediately, wait for user interaction
      const handleUserInteraction = () => {
        Notification.requestPermission().then(permission => {
          console.log('[App] Notification permission:', permission);
        });
        document.removeEventListener('click', handleUserInteraction);
      };
      document.addEventListener('click', handleUserInteraction, { once: true });
    }
  }, []);

  // Task 31: Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Keep the shell synchronized with same-tab login and logout changes.
  useEffect(() => {
    const refreshUserData = () => {
      const userStr = localStorage.getItem('spes_user');
      if (!userStr || !localStorage.getItem('spes_token')) {
        setUserData(null);
        return;
      }
      try {
        setUserData(JSON.parse(userStr));
      } catch (e) {
        console.error('Error parsing user data:', e);
        setUserData(null);
      }
    };

    refreshUserData();
    window.addEventListener(AUTH_STATE_EVENT, refreshUserData);
    window.addEventListener('storage', refreshUserData);
    return () => {
      window.removeEventListener(AUTH_STATE_EVENT, refreshUserData);
      window.removeEventListener('storage', refreshUserData);
    };
  }, []);

  // Task 31: Handle mobile navigation actions
  const handleMobileNavAction = useCallback((action, data) => {
    console.log('[MobileNav] Action:', action, data);
    // Handle mobile-specific navigation actions
    switch (action) {
      case 'refresh':
        window.location.reload();
        break;
      case 'notifications':
        // Navigate to notifications
        window.location.href = '/notifications';
        break;
      default:
        break;
    }
  }, []);

  // Task 31: Handle dashboard refresh
  const handleDashboardRefresh = useCallback(() => {
    console.log('[Dashboard] Refreshing data...');
    // Implement dashboard refresh logic
  }, []);

  return (
    <>
      {/* Task 30: PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Session Warning Modal */}
      <SessionModal
        show={showWarningModal}
        countdown={countdown}
        onStayLoggedIn={handleStayLoggedIn}
        onLogoutNow={handleLogoutNow}
      />

      {/* Logout Toast */}
      <LogoutToast
        show={showLogoutToast}
        message={logoutMessage}
        onClose={() => {}}
      />

      {/* Task 31: Mobile Drawer Navigation (shown on mobile only) */}
      {isMobile && userData && <MobileDrawer user={userData} />}

      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />  {/* Task 28: Forgot password OTP flow */}
        <Route path="/auth/callback" element={<AuthCallback />} />  {/* Task 24: OAuth callback */}
        <Route path="/reset-password" element={<PasswordResetPage />} />  {/* Task 26: Password reset */}
        
        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              {/* Task 31: Use different layout for mobile vs desktop */}
              {isMobile ? (
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route 
                    path="/dashboard" 
                    element={
                      <DashboardMobile 
                        userData={userData || {}}
                        onRefresh={handleDashboardRefresh}
                      />
                    } 
                  />
                  <Route path="/dashboard-v2" element={<DashboardV2 />} />  {/* Task 35: Dashboard 2.0 */}
                  <Route path="/admin-dashboard" element={<AdminDashboard />} />  {/* Task 26: Admin dashboard */}
                  <Route path="/manager-dashboard" element={<ManagerDashboard />} />  {/* Task 26: Manager dashboard */}
                  <Route path="/candidates" element={<Candidates />} />
                  <Route path="/follow-ups" element={<FollowUps />} />
                  <Route path="/my-followups" element={<MyFollowUps />} />  {/* Task 36: My Follow-Ups */}
                  <Route path="/upload" element={<UploadPage />} />
                  <Route path="/jd-upload" element={<JDUpload />} />
                  <Route path="/jd/:jdId" element={<JDDetail />} />  {/* Task 20: JD detail with matching */}
                  <Route path="/notifications" element={<Notifications />} />  {/* Task 21: Notifications page */}
                  <Route path="/settings" element={<Settings />} />  {/* Task 21: Settings page */}
                  <Route path="/reports" element={<Reports />} />
                </Routes>
              ) : (
                <Layout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/dashboard-v2" element={<DashboardV2 />} />  {/* Task 35: Dashboard 2.0 */}
                    <Route path="/admin-dashboard" element={<AdminDashboard />} />  {/* Task 26: Admin dashboard */}
                    <Route path="/manager-dashboard" element={<ManagerDashboard />} />  {/* Task 26: Manager dashboard */}
                    <Route path="/candidates" element={<Candidates />} />
                    <Route path="/follow-ups" element={<FollowUps />} />
                    <Route path="/my-followups" element={<MyFollowUps />} />  {/* Task 36: My Follow-Ups */}
                    <Route path="/upload" element={<UploadPage />} />
                    <Route path="/jd-upload" element={<JDUpload />} />
                    <Route path="/jd/:jdId" element={<JDDetail />} />  {/* Task 20: JD detail with matching */}
                    <Route path="/notifications" element={<Notifications />} />  {/* Task 21: Notifications page */}
                    <Route path="/settings" element={<Settings />} />  {/* Task 21: Settings page */}
                    <Route path="/reports" element={<Reports />} />
                  </Routes>
                </Layout>
              )}
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
